// src/screens/auth/RegisterScreen.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../../components/auth/AuthLayout";
import InputField from "../../components/common/InputField";
import Button from "../../components/common/Button";
import SocialDivider from "../../components/auth/SocialDivider";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";

/* ---- Inline icon helpers ---- */
const UserIcon = ({ size = 17, strokeWidth = 2 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);
const MailIcon = ({ size = 17, strokeWidth = 2 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);
const LockIcon = ({ size = 17, strokeWidth = 2 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);
const PhoneIcon = ({ size = 17, strokeWidth = 2 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);
const BadgeIcon = ({ size = 17, strokeWidth = 2 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0M9 12h6m-3-3v6" />
  </svg>
);
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);
const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

/* ---- Password strength meter ---- */
const strengthLevels = [
  { label: "Weak", color: "bg-red-400", width: "w-1/4" },
  { label: "Fair", color: "bg-orange-400", width: "w-2/4" },
  { label: "Good", color: "bg-yellow-400", width: "w-3/4" },
  { label: "Strong", color: "bg-green-400", width: "w-full" },
];

const getStrength = (pwd) => {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return score;
};

const PasswordStrength = ({ password }) => {
  if (!password) return null;
  const score = getStrength(password);
  const level = strengthLevels[Math.max(0, score - 1)];
  return (
    <div className="mt-2 space-y-1 animate-fade-in">
      <div className="flex gap-1 h-1.5">
        {strengthLevels.map((l, i) => (
          <div
            key={i}
            className={`flex-1 rounded-full transition-all duration-300 ${i < score ? level.color : "bg-slate-200"}`}
          />
        ))}
      </div>
      <p className="text-xs font-semibold" style={{ color: score <= 1 ? "#ef4444" : score === 2 ? "#f97316" : score === 3 ? "#eab308" : "#22c55e" }}>
        {level.label} password
      </p>
    </div>
  );
};

/* ---- Role selector ---- */
const roles = [
  { id: "student", label: "Student", desc: "Looking for internships", icon: "🎓" },
  { id: "company", label: "Company", desc: "Hiring interns", icon: "🏢" },
  { id: "mentor", label: "Mentor", desc: "Guiding talent", icon: "💡" },
];

/* ---- Main Component ---- */
const RegisterScreen = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState("student");
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "",
    phone: "", usn: "", password: "", confirmPassword: "", college: ""
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleChange = (field) => (e) => {
    let val = e.target.value;
    if (field === "phone") {
      val = val.replace(/\D/g, "");
      if (val.length > 10) val = val.slice(0, 10);
    }
    setForm((p) => ({ ...p, [field]: val }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = "First name is required";
    if (!form.lastName.trim()) errs.lastName = "Last name is required";
    if (!form.email) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = "Enter a valid email";
    if (!form.phone.trim()) errs.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(form.phone)) errs.phone = "Enter a valid 10-digit phone number";
    if (role === "student") {
      if (!form.usn.trim()) errs.usn = "USN Number is required";
      else if (!/^[A-Za-z0-9]{5,20}$/.test(form.usn.trim())) errs.usn = "Enter a valid USN (e.g. 1SI21CS001)";
    }
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!form.password) errs.password = "Password is required";
    else if (form.password.length < 8) errs.password = "Password must be at least 8 characters";
    if (!form.confirmPassword) errs.confirmPassword = "Please confirm your password";
    else if (form.password !== form.confirmPassword) errs.confirmPassword = "Passwords do not match";
    if (!agreed) errs.agreed = "You must agree to the terms";
    return errs;
  };

  const handleNext = () => {
    const errs = validateStep1();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateStep2();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
      const user = userCredential.user;

      // Save user data to Firestore
      await setDoc(doc(db, "internshipusers", user.uid), {
        uid: user.uid,
        role: role,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        ...(role === "student" && { usn: form.usn }),
        college: form.college,
        createdAt: new Date().toISOString()
      });

      alert("Account created successfully!");
      navigate("/dashboard"); // or wherever the user should go next
    } catch (error) {
      console.error("Registration error:", error);
      let errorMessage = "Failed to create account.";
      if (error.code === "auth/email-already-in-use") {
        errorMessage = "Email is already in use.";
      }
      setErrors((prev) => ({ ...prev, confirmPassword: errorMessage })); // Show generic error under confirm password
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={step === 1 ? "Create your account" : "Secure your account"}
      subtitle={
        step === 1
          ? "Join thousands of interns & companies on Quinternship"
          : "Set a strong password to protect your account"
      }
      illustrationTitle={"Start Your\nInternship Journey"}
      illustrationSubtitle="Create a free account and connect with top companies looking for talented interns like you."
    >
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-7">
        {[1, 2].map((s) => (
          <React.Fragment key={s}>
            <div
              className={[
                "flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all duration-300",
                step >= s
                  ? "bg-primary-500 text-white shadow-btn"
                  : "bg-slate-200 text-slate-400",
              ].join(" ")}
            >
              {step > s ? (
                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : s}
            </div>
            {s < 2 && (
              <div className={`flex-1 h-0.5 rounded-full transition-all duration-500 ${step > s ? "bg-primary-500" : "bg-slate-200"}`} />
            )}
          </React.Fragment>
        ))}
        <span className="text-xs font-semibold text-slate-400 ml-1">Step {step} of 2</span>
      </div>

      <form id="register-form" onSubmit={step === 1 ? (e) => { e.preventDefault(); handleNext(); } : handleSubmit} noValidate>
        {step === 1 ? (
          <div className="space-y-4 animate-fade-in-up">
            {/* Role selector */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">I am a</label>
              <div className="grid grid-cols-3 gap-2">
                {/* Default: Student only — Company & Mentor commented out for now */}
                <button
                  type="button"
                  id="role-student-btn"
                  onClick={() => setRole("student")}
                  className={[
                    "flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer",
                    role === "student"
                      ? "border-primary-400 bg-primary-50 shadow-sm"
                      : "border-white/70 bg-white/50 hover:border-primary-200 hover:bg-white/70",
                  ].join(" ")}
                >
                  <span className="text-xl">🎓</span>
                  <span className={`text-xs font-bold ${role === "student" ? "text-primary-600" : "text-slate-600"}`}>Student</span>
                  <span className="text-[10px] text-slate-400 leading-tight">Looking for internships</span>
                </button>

                {/* <button
                  type="button"
                  id="role-company-btn"
                  onClick={() => setRole("company")}
                  className={[
                    "flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer",
                    role === "company"
                      ? "border-primary-400 bg-primary-50 shadow-sm"
                      : "border-white/70 bg-white/50 hover:border-primary-200 hover:bg-white/70",
                  ].join(" ")}
                >
                  <span className="text-xl">🏢</span>
                  <span className={`text-xs font-bold ${role === "company" ? "text-primary-600" : "text-slate-600"}`}>Company</span>
                  <span className="text-[10px] text-slate-400 leading-tight">Hiring interns</span>
                </button> */}

                {/* <button
                  type="button"
                  id="role-mentor-btn"
                  onClick={() => setRole("mentor")}
                  className={[
                    "flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer",
                    role === "mentor"
                      ? "border-primary-400 bg-primary-50 shadow-sm"
                      : "border-white/70 bg-white/50 hover:border-primary-200 hover:bg-white/70",
                  ].join(" ")}
                >
                  <span className="text-xl">💡</span>
                  <span className={`text-xs font-bold ${role === "mentor" ? "text-primary-600" : "text-slate-600"}`}>Mentor</span>
                  <span className="text-[10px] text-slate-400 leading-tight">Guiding talent</span>
                </button> */}
              </div>
            </div>

            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <InputField
                id="register-first-name"
                label="First Name"
                type="text"
                placeholder="John"
                value={form.firstName}
                onChange={handleChange("firstName")}
                icon={UserIcon}
                error={errors.firstName}
                required
                autoComplete="given-name"
              />
              <InputField
                id="register-last-name"
                label="Last Name"
                type="text"
                placeholder="Doe"
                value={form.lastName}
                onChange={handleChange("lastName")}
                error={errors.lastName}
                required
                autoComplete="family-name"
              />
            </div>

            <InputField
              id="register-email"
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange("email")}
              icon={MailIcon}
              error={errors.email}
              required
              autoComplete="email"
            />

            <InputField
              id="register-phone"
              label="Phone Number"
              type="tel"
              placeholder="98765 43210"
              value={form.phone}
              onChange={handleChange("phone")}
              icon={PhoneIcon}
              error={errors.phone}
              required
              autoComplete="tel"
              maxLength="10"
            />

            {role === "student" && (
              <InputField
                id="register-usn"
                label="USN Number"
                type="text"
                placeholder="e.g. 1SI21CS001"
                value={form.usn}
                onChange={handleChange("usn")}
                icon={BadgeIcon}
                error={errors.usn}
                required
                autoComplete="off"
              />
            )}

            <div>
              <label htmlFor="register-college" className="block text-sm font-semibold text-slate-700 tracking-wide mb-1.5">
                College Name <span className="text-primary-500 ml-1">*</span>
              </label>
              <select
                id="register-college"
                value={form.college}
                onChange={handleChange("college")}
                className="w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-800 bg-white/60 border border-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 focus:bg-white/90 transition-all duration-300 hover:border-primary-200"
                required
              >
                <option value="" disabled>Select College</option>
                <option value="SSIBM Tumakur">SSIBM Tumakur</option>
                {/* <option value="keep now">keep now</option> */}
                {/* <option value="Other">Other</option> */}
              </select>
            </div>

            <Button id="register-next-btn" type="submit" variant="primary" size="lg" fullWidth>
              Continue
            </Button>

            {/* Social login — hidden for now, enable when ready */}
            {false && (
              <>
                <SocialDivider />
                <div className="grid grid-cols-2 gap-3">
                  <button
                    id="google-register-btn"
                    type="button"
                    className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-white/70 bg-white/60 backdrop-blur-sm hover:bg-white/80 hover:border-slate-200 transition-all duration-300 text-sm font-semibold text-slate-700 shadow-sm"
                  >
                    <GoogleIcon />
                    Google
                  </button>
                  <button
                    id="linkedin-register-btn"
                    type="button"
                    className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-white/70 bg-white/60 backdrop-blur-sm hover:bg-white/80 hover:border-slate-200 transition-all duration-300 text-sm font-semibold text-slate-700 shadow-sm"
                  >
                    <LinkedInIcon />
                    LinkedIn
                  </button>
                </div>
              </>
            )}

            <p className="text-center text-sm text-slate-500 font-medium pt-1">
              Already have an account?{" "}
              <Link to="/login" id="go-to-login-link" className="text-primary-500 font-bold hover:text-primary-700 transition-colors duration-200">
                Sign in
              </Link>
            </p>
          </div>
        ) : (
          <div className="space-y-4 animate-fade-in-up">
            <div>
              <InputField
                id="register-password"
                label="Password"
                type="password"
                placeholder="Create a strong password"
                value={form.password}
                onChange={handleChange("password")}
                icon={LockIcon}
                error={errors.password}
                required
                autoComplete="new-password"
              />
              <PasswordStrength password={form.password} />
            </div>

            <InputField
              id="register-confirm-password"
              label="Confirm Password"
              type="password"
              placeholder="Repeat your password"
              value={form.confirmPassword}
              onChange={handleChange("confirmPassword")}
              icon={LockIcon}
              error={errors.confirmPassword}
              required
              autoComplete="new-password"
            />

            {/* Terms */}
            <div>
              <label className="flex items-start gap-2.5 cursor-pointer select-none group" htmlFor="terms-checkbox">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    id="terms-checkbox"
                    type="checkbox"
                    className="sr-only"
                    checked={agreed}
                    onChange={() => { setAgreed((v) => !v); if (errors.agreed) setErrors((p) => ({ ...p, agreed: "" })); }}
                  />
                  <div
                    className={[
                      "w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200",
                      agreed ? "bg-primary-500 border-primary-500" : "border-slate-300 bg-white/60 group-hover:border-primary-300",
                    ].join(" ")}
                  >
                    {agreed && (
                      <svg width="10" height="8" fill="none" viewBox="0 0 10 8">
                        <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm text-slate-600 font-medium leading-snug">
                  I agree to QuBright&apos;s{" "}
                  <Link to="/terms" className="text-primary-500 hover:underline font-semibold">Terms of Service</Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-primary-500 hover:underline font-semibold">Privacy Policy</Link>
                </span>
              </label>
              {errors.agreed && (
                <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                  </svg>
                  {errors.agreed}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                id="register-back-btn"
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 py-3 rounded-xl border-2 border-slate-200 bg-white/60 text-slate-600 font-semibold text-sm hover:border-primary-300 hover:text-primary-600 transition-all duration-300"
              >
                Back
              </button>
              <Button id="register-submit-btn" type="submit" variant="primary" size="lg" loading={loading} className="flex-1">
                {loading ? "Creating..." : "Create Account"}
              </Button>
            </div>
          </div>
        )}
      </form>
    </AuthLayout>
  );
};

export default RegisterScreen;
